/**
 * ConditionsPreviewButton Component
 *
 * A button that shows a popover with a visual representation of conditions.
 * Used to show a preview of segment/campaign conditions in audit logs.
 */

import React from 'react';
import { Text, Box } from '@customerio/pluma-components/react';
import { ConditionViewer } from './condition-viewer';
import { ConditionPopover } from './condition-popover';
import { type RelatedLogEntry } from './constants';
import { AppErrorBoundary } from '@/components/app-error-boundary';
import { isEmptyConditions, isJustReference, type MessageInfo } from './condition-utils';

// Lazy load ConditionWithFetch outside the component to prevent remounts
const LazyConditionWithFetch = React.lazy(() => import('./condition-with-fetch'));

interface ConditionsPreviewButtonProps {
  /** The conditions object to display */
  conditions: Record<string, unknown>;
  /** Label for the button */
  label?: string;
  /** Title for the popover header */
  title?: string;
  /** Popover placement */
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

export function ConditionsPreviewButton({
  conditions,
  label = 'View conditions',
  title = 'Conditions',
  placement = 'bottom',
}: ConditionsPreviewButtonProps) {
  if (isEmptyConditions(conditions)) {
    return (
      <Text size="s" color="subtle">
        No conditions
      </Text>
    );
  }

  return (
    <ConditionPopover title={title} placement={placement} buttonLabel={label}>
      <ConditionViewer condition={conditions} />
    </ConditionPopover>
  );
}

/**
 * Inline conditions preview that shows on hover.
 * Shows the formatted text with a hover preview of the full conditions tree.
 *
 * If the conditions are just a reference (condition_id), it will fetch the
 * actual condition details from the condition's audit log.
 */
interface ConditionsWithPreviewProps {
  /** The conditions object to display */
  conditions: Record<string, unknown>;
  /** The formatted text to display inline (can be string or React nodes with links) */
  formattedText: string | React.ReactNode;
  /** Title for the popover header */
  title?: string;
  /** Popover placement */
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /** Workspace ID for fetching condition details (required when conditions is just a reference) */
  workspaceId?: number;
  /** Pre-fetched related logs (condition details) from the backend */
  relatedLogs?: RelatedLogEntry[];
}

export function ConditionsWithPreview({
  conditions,
  formattedText,
  title = 'Conditions',
  placement = 'bottom',
  workspaceId,
  relatedLogs,
}: ConditionsWithPreviewProps) {
  if (isEmptyConditions(conditions)) {
    return (
      <Text size="s" color="subtle">
        {typeof formattedText === 'string' ? formattedText : 'No conditions'}
      </Text>
    );
  }

  // Check if this is just a reference to a condition (no actual condition details)
  const justReference = isJustReference(conditions);
  const conditionId = justReference && 'condition_id' in conditions ? (conditions.condition_id as number) : null;

  // If it's a condition_id reference and we have workspaceId, use ConditionWithFetch
  if (justReference && conditionId != null && conditionId > 0 && workspaceId != null && workspaceId > 0) {
    const errorFallback = (
      <Text size="s" color="subtle">
        {typeof formattedText === 'string' ? formattedText : `Condition #${conditionId}`} (failed to load)
      </Text>
    );

    return (
      <AppErrorBoundary fallback={errorFallback}>
        <React.Suspense
          fallback={
            <Text size="s" color="subtle">
              {formattedText}
            </Text>
          }
        >
          <LazyConditionWithFetch
            conditionId={conditionId}
            workspaceId={workspaceId}
            displayText={typeof formattedText === 'string' ? formattedText : `Condition #${conditionId}`}
            inverse={conditions.inverse === true}
            placement={placement}
          />
        </React.Suspense>
      </AppErrorBoundary>
    );
  }

  return (
    <Box display="flex" alignItems="center" gap="100">
      <Text size="s" color="subtle" overflowWrap="anywhere">
        {formattedText}
      </Text>
      <ConditionPopover title={title} placement={placement}>
        <ConditionViewer condition={conditions} workspaceId={workspaceId} relatedLogs={relatedLogs} />
      </ConditionPopover>
    </Box>
  );
}

// =============================================================================
// Inline Conditions Display (no popover)
// =============================================================================

interface ConditionsInlineProps {
  /** The conditions object to display */
  conditions: Record<string, unknown>;
  /** Workspace ID for fetching condition details */
  workspaceId?: number;
  /** Pre-fetched related logs (condition details) from the backend */
  relatedLogs?: RelatedLogEntry[];
  /** Optional campaigns list for looking up campaign names in message conditions */
  campaigns?: Array<{ id: number; name: string }>;
  /** Optional segments list for looking up segment names in segment conditions */
  segments?: Array<{ id: number; name: string }>;
  /** Optional form ID to name mappings (from segment's linked_form_id -> name) */
  formNames?: Record<string, string>;
  /** Optional object types for relationship conditions */
  objectTypes?: Array<{ id: number; name: string }>;
  /** Optional newsletters list for looking up newsletter names in newsletter_recipients conditions */
  newsletters?: Array<{ id: number; name: string }>;
  /** Optional messages list for looking up message names by output_id */
  messages?: MessageInfo[];
}

/**
 * Renders conditions directly inline without a popover.
 * Shows the full visual conditions tree under the field name.
 */
export function ConditionsInline({
  conditions,
  workspaceId,
  relatedLogs,
  campaigns,
  segments,
  formNames,
  objectTypes,
  newsletters,
  messages,
}: ConditionsInlineProps) {
  if (isEmptyConditions(conditions)) {
    return (
      <Text size="s" color="subtle">
        No conditions
      </Text>
    );
  }

  return (
    <ConditionViewer
      condition={conditions}
      workspaceId={workspaceId}
      relatedLogs={relatedLogs}
      campaigns={campaigns}
      segments={segments}
      formNames={formNames}
      objectTypes={objectTypes}
      newsletters={newsletters}
      messages={messages}
    />
  );
}

export default ConditionsPreviewButton;
